import BigNumber from 'bignumber.js';
import { FACTOR_PRICE_DECIMAL } from "@libs/shared/constants/codex";
import { IPair, IPairDto, IPairSettings, ISettingsState, PairSettingsIds } from '../interfaces';
import { AccountAssetType, CodexAsset, CodexBalance, CodexBalanceDto, IOrderDto, Order, OrderStatus } from './types';
import assetsJson from '../assets/assetsByUname.json';
import { amountValueFormate, convertFeeToPrecent, equalsIgnoreCase, roundTo } from './comman';

export const joinPairToOrder = (orders: IOrderDto[], pairs: IPair[]): Order[] => {
  var pairsMap = new Map(pairs.map((pair) => [+pair.id, pair]));
  return orders?.reduce((acc, o) => {
    const pair = pairsMap.get(o.pairId);
    if (pair) {
      const order = {
        ...o,
        executed: o.status !== OrderStatus.Open,
        base_decimal: pair?.base_decimal,
        price_decimal: pair.price_decimal,
        base_currency: pair?.base_currency?.toUpperCase(),
        price_currency: pair?.price_currency?.toUpperCase(),
        min_size_increment: pair?.min_size_increment,
        min_price_increment: pair?.min_price_increment,
        price_id: pair?.price_id,
      };
      acc.push(order);
    }
    return acc;
  }, []);
}

export const notRestrictedPairs = (pairs: IPairDto[], currentCountry: string | undefined) => {
  if(!currentCountry) return pairs;
  return pairs.filter(el => !el.restrictedCountries || !el.restrictedCountries.find(country => equalsIgnoreCase(country, currentCountry)));
};

export const adjustFee = (fee: number, minFee: number) => {
  if (!fee) return 0;
  return convertFeeToPrecent(BigNumber(fee).plus(minFee).toString());
}

export const atomicToDecimal = (value: string, decimal: number = FACTOR_PRICE_DECIMAL) => {
  return value ? amountValueFormate(value, decimal) : "0";
}

export const buildPair = (pairDto: IPairDto, settings: Pick<ISettingsState, 'minFee'>): IPair => {
  const makerBaseFee = pairDto.pairSettings[PairSettingsIds.MAKER_FEE] || null;
  const takerBaseFee = pairDto.pairSettings[PairSettingsIds.TAKER_FEE] || null;
  return {
    ...pairDto,
    pairSettings: {
      mftTitle: pairDto.pairSettings[PairSettingsIds.MFT_TITLE] || "",
      baseCoinIconLink: pairDto.pairSettings[PairSettingsIds.VIEW_BASE_COIN_ICON_LINK] || "",
      audioLink: pairDto.pairSettings[PairSettingsIds.MFT_AUDIO_LINK] || "",
      baseCoinMarketLink: pairDto.pairSettings[PairSettingsIds.VIEW_BASE_COIN_MARKET_LINK] || "",
      priceCoinMarketLink: pairDto.pairSettings[PairSettingsIds.VIEW_PRICE_COIN_MARKET_LINK] || "",
      preSaleMode: pairDto.pairSettings[PairSettingsIds.MODE_PRE_SALE] || null,
      makerFee: makerBaseFee && adjustFee(+makerBaseFee, settings.minFee),
      takerFee: takerBaseFee && adjustFee(+takerBaseFee, settings.minFee),
    } as IPairSettings,
    current_price: atomicToDecimal(pairDto.current_price),
    h: atomicToDecimal(pairDto.h),
    l: atomicToDecimal(pairDto.l),
    l_p: pairDto.l_p ? atomicToDecimal(pairDto.l_p) : "",
    price_24: atomicToDecimal(pairDto.price_24),
    total_24: atomicToDecimal(pairDto.total_24),
    volume_24: atomicToDecimal(pairDto.volume_24, pairDto.base_decimal),
    change_24: pairDto.change_24 ? Number(roundTo(pairDto.change_24, 2)) : 0,
  }
}

export const getActualElement = (oldArray: any[], newArray: any[], filterFnc: (a, b) => boolean) => {
  return newArray.map(newItem => {
    const exsistItem = oldArray.find((oldItem: Order) => filterFnc(oldItem, newItem));
    if (exsistItem && new Date(exsistItem.updatedAt).getTime() >= new Date(newItem.updatedAt).getTime()) {
      return exsistItem;
    }
    return newItem;
  });
}

export const bigIntReplacer = (value) => {
  if (typeof value === 'bigint') {
    return value.toString();
  }
  return value;
};

export const isObjectEqual = (obj1, obj2) => {
  return (
    JSON.stringify(obj1, bigIntReplacer) ===
    JSON.stringify(obj2, bigIntReplacer)
  );
};

export const formatBigNumberToString = (labelValue) => {
  return Math.abs(Number(labelValue)) >= 1.0e9
    ? parseFloat((Math.abs(Number(labelValue)) / 1.0e9).toFixed(6)) + 'B'
    : Math.abs(Number(labelValue)) >= 1.0e6
    ? parseFloat((Math.abs(Number(labelValue)) / 1.0e6).toFixed(4)) + 'M'
    : Math.abs(Number(labelValue)) >= 1.0e3
    ? parseFloat((Math.abs(Number(labelValue)) / 1.0e3).toFixed(2)) + 'K'
    : Number(Number(labelValue).toString().split('.')[0])
    ? parseFloat(Math.abs(Number(labelValue)).toFixed(2))
    : parseFloat(Math.abs(Number(labelValue)).toFixed(4));
};

export function addBalanceToAsset(asset:AccountAssetType, balance?: CodexBalance) {
  return {
    ...asset,
    amount: balance?.availableAmount || "",
    lockedAmount: balance?.lockedAmount || "",
  }
}

export function mapAsset(asset: CodexAsset, balance?: CodexBalance): AccountAssetType {
  const assetImage = getAssetImage({
    address: asset.address,
    chainId: asset.chainId,
    unitName: asset.unitName
  });
  return {
    id: asset.id,
    index: asset.address,
    name: asset.name,
    decimal: asset.decimals,
    img: asset.img || assetImage,
    amount: balance?.availableAmount || "",
    lockedAmount: balance?.lockedAmount || "",
    unit_name: asset.unitName,
    usd_value: 0,
    chainId: asset.chainId,
    isWrapped: asset.isGas,
  } as AccountAssetType;
}

export function mapToCodexBalance(balance: CodexBalanceDto): CodexBalance {
  return {
    loginAddress: balance.loginAddress,
    loginChainId: balance.loginChainId,
    tokenId: balance.tokenAddress,
    tokenChainId: balance.tokenChainId,
    availableAmount: BigNumber(balance.amount).minus(balance.lockedAmount).toString(),
    lockedAmount: balance.lockedAmount,
  }
}

const trustWalletChains = {
  8: 'algorand',
  6: 'avalanchec',
  23: 'arbitrum',
  24: 'optimism',
  30: 'base',
  2: 'ethereum',
  1: 'solana',
  5: 'polygon',
  4: 'binance',
};

// Move this to backend TO DO
export const getAssetImage = ({address, chainId, unitName}: {address: string, chainId: number, unitName: string}) => {
  const asset = assetsJson[unitName];
  if (asset && asset.logo && asset.logo.png) {
    return asset.logo.png;
  } else {
    const url = `https://raw.githubusercontent.com/trustwallet/assets/refs/heads/master/blockchains/${trustWalletChains[chainId]}/assets/${address}/logo.png`;
    return url;
  }
};
